// SPDX-License-Identifier: MIT

import React from 'react';
import { createRoot } from 'react-dom/client';

import ConsentManager from './ConsentManager';
import { resolveConsentTypes } from './resolveTrackers';
import { reportWebVitals } from './webVitals';

// Report Core Web Vitals to dataLayer when enabled (replaces
// gatsby-plugin-google-tagmanager's enableWebVitalsTracking). Measured for every
// visitor — it sets no cookies; GTM only forwards it once consent loads GTM.
export const onClientEntry = (_, pluginOptions = {}) => {
  if (pluginOptions.enableWebVitalsTracking) reportWebVitals();
};

// Mount the banner in its own React root appended to <body>, not via
// wrapRootElement — Gatsby applies wrapRootElement to the Head render too, so UI
// there triggers "invalid head element" warnings (gatsby.dev/invalid-head-elements).
// A dedicated root keeps the banner out of the page/head trees, and it persists
// across SPA navigation (mounted once). The banner is self-contained and talks to
// the rest of the app through CONSENT_EVENT, so it needs no app context.
export const onInitialClientRender = (_, pluginOptions = {}) => {
  if (typeof document === 'undefined' || document.getElementById('cc-container')) return;
  const { consentTypes } = resolveConsentTypes(pluginOptions.consentTypes);
  const config = { ...pluginOptions, consentTypes };

  const container = document.createElement('div');
  container.id = 'cc-container';
  document.body.appendChild(container);
  createRoot(container).render(<ConsentManager config={config} />);
};

// Fire consent-gated tracker page views on SPA navigation. Options are passed to
// this hook too, so trackers are rebuilt from the same specs (cheap, stateless);
// each handler is a no-op until its consent loaded the tracker. The entry page
// view is fired by onAccept, so skip the initial route (prevLocation is null).
export const onRouteUpdate = ({ prevLocation }, pluginOptions = {}) => {
  if (!prevLocation) return;
  const { onRouteChange } = resolveConsentTypes(pluginOptions.consentTypes);
  onRouteChange();
};
